import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

const slideUp = keyframes`from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; }`;

const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 1200;
  display: flex; align-items: flex-end; justify-content: center;
`;
const Panel = styled.div`
  background: white; border-radius: 20px 20px 0 0; width: 100%; max-width: 600px;
  max-height: 90vh; display: flex; flex-direction: column;
  animation: ${slideUp} 0.25s ease;
`;
const PanelHeader = styled.div`
  padding: 20px 20px 12px; border-bottom: 1px solid #f3f4f6;
  display: flex; align-items: center; justify-content: space-between;
`;
const PanelTitle = styled.h2`font-size: 18px; font-weight: 700; color: #111827; margin: 0; display: flex; align-items: center; gap: 9px;`;
const CloseBtn = styled.button`
  background: #f3f4f6; border: none; border-radius: 50%; width: 32px; height: 32px;
  font-size: 16px; cursor: pointer; display: flex; align-items: center; justify-content: center;
`;
const Body = styled.div`flex: 1; overflow-y: auto; padding: 16px 20px;`;
const Footer = styled.div`padding: 16px 20px; border-top: 1px solid #f3f4f6;`;

const CartItem = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 10px 0; border-bottom: 1px solid #f9fafb;
  &:last-child { border-bottom: none; }
`;
const ItemName = styled.div`flex: 1; font-size: 14px; font-weight: 500; color: #111827;`;
const ItemPrice = styled.div`font-size: 14px; color: #6b7280; min-width: 55px; text-align: right;`;
const QtyControl = styled.div`display: flex; align-items: center; gap: 8px;`;
const QtyBtn = styled.button`
  width: 28px; height: 28px; border-radius: 50%; border: 1px solid #d1d5db;
  background: white; font-size: 16px; cursor: pointer; display: flex; align-items: center; justify-content: center;
  &:hover { background: #f3f4f6; }
`;
const QtyNum = styled.span`font-size: 14px; font-weight: 600; min-width: 20px; text-align: center;`;

const FormGroup = styled.div`margin-bottom: 12px;`;
const Label = styled.label`font-size: 13px; font-weight: 500; color: #374151; display: block; margin-bottom: 4px;`;
const Input = styled.input`
  width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px;
  font-size: 14px; box-sizing: border-box;
  &:focus { outline: none; border-color: #22c55e; box-shadow: 0 0 0 3px rgba(34,197,94,0.1); }
`;
const Textarea = styled.textarea`
  width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px;
  font-size: 14px; box-sizing: border-box; resize: none; height: 72px;
  &:focus { outline: none; border-color: #22c55e; box-shadow: 0 0 0 3px rgba(34,197,94,0.1); }
`;
const SendBtn = styled.button`
  width: 100%; padding: 14px; background: #25d366; color: white; border: none;
  border-radius: 12px; font-size: 16px; font-weight: 700; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  &:hover { background: #1ebe5d; }
  &:disabled { background: #d1d5db; cursor: not-allowed; }
`;
const Total = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  font-size: 16px; font-weight: 700; color: #111827; margin-bottom: 16px;
`;
const EmptyMsg = styled.div`text-align: center; color: #9ca3af; padding: 32px 0; font-size: 14px;`;
const ErrorMsg = styled.div`color: #991b1b; font-size: 13px; margin-bottom: 8px;`;

export interface CartItem {
  lineId: string;
  id: number;
  name: string;
  price?: number;
  quantity: number;
  notes?: string;
}

interface Props {
  items: CartItem[];
  onUpdateQty: (lineId: string, qty: number) => void;
  onClose: () => void;
  whatsappNumber: string;
  businessName: string;
}

const AsportoCart: React.FC<Props> = ({ items, onUpdateQty, onClose, whatsappNumber, businessName }) => {
  const [name, setName] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  const total = items.reduce((sum, i) => sum + (i.price || 0) * i.quantity, 0);
  const hasItems = items.some(i => i.quantity > 0);
  const activeItems = items.filter(i => i.quantity > 0);

  const handleSend = () => {
    if (!name.trim()) { setError('Inserisci il tuo nome'); return; }
    if (!pickupTime.trim()) { setError('Inserisci l\'orario di ritiro'); return; }
    setError('');

    const lines = activeItems.map(i =>
      `• ${i.quantity}x ${i.name}${i.price ? ` (${(i.price * i.quantity).toFixed(2)}€)` : ''}${i.notes ? `\n   ↳ ${i.notes}` : ''}`
    ).join('\n');

    const totalLine = total > 0 ? `\n💰 *Totale: ${total.toFixed(2)}€*` : '';
    const notesLine = notes.trim() ? `\n📝 Note: ${notes.trim()}` : '';

    const message =
      `🛍️ *Ordine Asporto — ${businessName}*\n\n` +
      `👤 Nome: ${name.trim()}\n` +
      `⏰ Ritiro: ${pickupTime.trim()}\n\n` +
      `*Prodotti:*\n${lines}` +
      totalLine +
      notesLine +
      `\n\nPagamento in sede al ritiro.`;

    const number = whatsappNumber.replace(/\s|\+/g, '');
    const url = `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  return (
    <Overlay onClick={onClose}>
      <Panel onClick={e => e.stopPropagation()}>
        <PanelHeader>
          <PanelTitle>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#cc9d6d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4H6z"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <path d="M16 10a4 4 0 0 1-8 0"/>
            </svg>
            Il tuo ordine asporto
          </PanelTitle>
          <CloseBtn onClick={onClose}>✕</CloseBtn>
        </PanelHeader>

        <Body>
          {!hasItems ? (
            <EmptyMsg>Nessun prodotto selezionato.<br />Torna al menu e aggiungi qualcosa!</EmptyMsg>
          ) : (
            <>
              {activeItems.map(item => (
                <CartItem key={item.lineId}>
                  <ItemName>
                    {item.name}
                    {item.notes && <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400, marginTop: 2 }}>📝 {item.notes}</div>}
                  </ItemName>
                  {item.price && <ItemPrice>{(item.price * item.quantity).toFixed(2)}€</ItemPrice>}
                  <QtyControl>
                    <QtyBtn onClick={() => onUpdateQty(item.lineId, item.quantity - 1)}>−</QtyBtn>
                    <QtyNum>{item.quantity}</QtyNum>
                    <QtyBtn onClick={() => onUpdateQty(item.lineId, item.quantity + 1)}>+</QtyBtn>
                  </QtyControl>
                </CartItem>
              ))}
            </>
          )}

          {hasItems && (
            <div style={{ marginTop: '20px' }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>
                I tuoi dati
              </div>
              {error && <ErrorMsg>{error}</ErrorMsg>}
              <FormGroup>
                <Label>Nome *</Label>
                <Input
                  type="text"
                  placeholder="Come ti chiami?"
                  value={name}
                  onChange={e => { setName(e.target.value); setError(''); }}
                />
              </FormGroup>
              <FormGroup>
                <Label>Orario di ritiro *</Label>
                <Input
                  type="text"
                  placeholder="Es. 13:00 oppure il prima possibile"
                  value={pickupTime}
                  onChange={e => { setPickupTime(e.target.value); setError(''); }}
                />
              </FormGroup>
              <FormGroup>
                <Label>Note (opzionale)</Label>
                <Textarea
                  placeholder="Allergie, variazioni, richieste speciali..."
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                />
              </FormGroup>
            </div>
          )}
        </Body>

        {hasItems && (
          <Footer>
            {total > 0 && (
              <Total>
                <span>Totale</span>
                <span>{total.toFixed(2)}€</span>
              </Total>
            )}
            <SendBtn onClick={handleSend}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white" style={{ flexShrink: 0 }}>
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Invia ordine su WhatsApp
            </SendBtn>
          </Footer>
        )}
      </Panel>
    </Overlay>
  );
};

export default AsportoCart;
